import React, { useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Localization } from "@viamap/viamap2-common";
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { GlassButton, GlassButtonRow } from "src/components/GlassButtons";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { PLAAttributeDefinition, PLA_CUBE_TOTAL_KEY, getPLACubeCellKey, getPLACubeCellValue, isPLANumberValueType, PLAElementRecord, PLAProject, setPLACubeCellValue } from "./PLAState";
import { formatPLAAttributeValue } from "./PLAFormatting";
import { comparePLAElementsByDefaultOrder } from "./PLAElementSorting";
import { PLACubeViewContext } from "./PLACubeViewContext";
import { applyPLACubeGlobalFilter, canShowPLACubeTotals, findPLACubeLatestCell, getPLACubePhaseTextColor, isPLACubeDimensionTotalEnabled, PLA_CUBE_ANY_FILTER } from "./PLACubeTimeView";
import type { PLACubeViewSettings } from "./PLACubeTimeView";
import { PLACubePhaseLegend } from "./PLACubeGlobalFilterDialog";
import { useFloatingPortalContext } from "src/componentsUtils/PortalHelpers";
import "./PLATable.css";

export { PLA_CUBE_ANY_FILTER } from "./PLACubeTimeView";
export type PLACubeFilter = Record<number, string>;

function hasSelectedCubeMember(filter: PLACubeFilter | undefined, index: number | undefined): boolean {
   if (index === undefined) return false;
   const memberId = filter?.[index];
   return Boolean(memberId && memberId !== PLA_CUBE_ANY_FILTER);
}

function normalizeCubeInstanceSelection(keys: string[] | undefined, dimensionOptions: ReturnType<typeof getPLACubeDimensionOptions>): string[] {
   return dimensionOptions.map((option, index) => {
      const existingKey = keys?.[index];
      return existingKey && option.elements.some((element) => element.id === existingKey) ? existingKey : option.elements[0]?.id || PLA_CUBE_TOTAL_KEY;
   });
}

function cubeInputValue(value: any): string {
   return value === undefined || value === null ? "" : String(value);
}

function parseCubeInputValue(value: string | boolean, definition: PLAAttributeDefinition): any {
   if (definition.valueType === "boolean") return value === true || value === "true";
   if (isPLANumberValueType(definition.valueType)) return value === "" ? "" : Number(value);
   return value;
}

export function getPLACubeDimensionOptions(project: PLAProject, elements: PLAElementRecord[], definition: PLAAttributeDefinition) {
   return (definition.cubeDimensions || []).map((dimension) => ({
      dimension,
      name: project.elementTypes[dimension.elementTypeId]?.name || dimension.elementTypeId,
      elements: elements.filter((element) => element.elementType === dimension.elementTypeId).sort(comparePLAElementsByDefaultOrder)
   }));
}

export function getPLACubeElementName(element: PLAElementRecord): string {
   return element.name || element.id;
}

function getCubeFilterCombinations(dimensionOptions: ReturnType<typeof getPLACubeDimensionOptions>, filter: PLACubeFilter | undefined): string[][] {
   return dimensionOptions.reduce<string[][]>((combinations, option, dimensionIndex) => {
      const selectedKey = filter?.[dimensionIndex];
      const keys = selectedKey && selectedKey !== PLA_CUBE_ANY_FILTER ? [selectedKey] : option.elements.map((element) => element.id);
      return combinations.flatMap((combination) => (keys.length ? keys : [PLA_CUBE_TOTAL_KEY]).map((key) => [...combination, key]));
   }, [[]]);
}

export function getPLACubeFilteredValue(value: any, definition: PLAAttributeDefinition, project: PLAProject, elements: PLAElementRecord[], filter?: PLACubeFilter): any {
   const dimensionOptions = getPLACubeDimensionOptions(project, elements, definition);
   if (!isPLANumberValueType(definition.valueType)) {
      const keys = dimensionOptions.map((_option, dimensionIndex) => {
         const selectedKey = filter?.[dimensionIndex];
         return selectedKey && selectedKey !== PLA_CUBE_ANY_FILTER ? selectedKey : PLA_CUBE_TOTAL_KEY;
      });
      return getPLACubeCellValue(value, keys);
   }

   let hasValue = false;
   const total = getCubeFilterCombinations(dimensionOptions, filter).reduce((sum, keys) => {
      const numericValue = Number(getPLACubeCellValue(value, keys));
      if (!Number.isFinite(numericValue)) return sum;
      hasValue = true;
      return sum + numericValue;
   }, 0);
   if (hasValue) return total;

   const totalKeys = dimensionOptions.map((_option, dimensionIndex) => {
      const selectedKey = filter?.[dimensionIndex];
      return selectedKey && selectedKey !== PLA_CUBE_ANY_FILTER ? selectedKey : PLA_CUBE_TOTAL_KEY;
   });
   return getPLACubeCellValue(value, totalKeys);
}

export function getPLACubeLatestFilteredTotal(value: any, definition: PLAAttributeDefinition, elements: PLAElementRecord[], filter: PLACubeFilter | undefined, settings: PLACubeViewSettings): { value: number; oldestPhaseIndex?: number } | undefined {
   const dimensions = definition.cubeDimensions || [];
   const timeDimensionIndex = dimensions.findIndex((dimension) => dimension.elementTypeId === settings.dimensionId);
   if (!settings.latest || timeDimensionIndex < 0) return undefined;
   const phases = elements.filter((element) => element.elementType === dimensions[timeDimensionIndex].elementTypeId).sort(comparePLAElementsByDefaultOrder);
   const members = dimensions.map((dimension, index) => {
      if (index === timeDimensionIndex) return undefined;
      const selected = filter?.[index];
      if (selected && selected !== PLA_CUBE_ANY_FILTER) return [selected];
      const elementsForDimension = elements.filter((element) => element.elementType === dimension.elementTypeId).map((element) => element.id);
      return elementsForDimension.length ? elementsForDimension : [PLA_CUBE_TOTAL_KEY];
   });
   const otherIndexes = dimensions.map((_dimension, index) => index).filter((index) => index !== timeDimensionIndex);
   const combinations = otherIndexes.reduce<string[][]>((combinationsForPrevious, index) =>
      combinationsForPrevious.flatMap((combination) => (members[index] || [PLA_CUBE_TOTAL_KEY]).map((member) => [...combination, member])), [[]]);
   let total = 0;
   let hasValue = false;
   let oldestPhaseIndex: number | undefined;
   combinations.forEach((combination) => {
      const keys = Array<string>(dimensions.length).fill(PLA_CUBE_TOTAL_KEY);
      otherIndexes.forEach((index, position) => { keys[index] = combination[position]; });
      const latest = findPLACubeLatestCell(keys, timeDimensionIndex, phases, settings.memberId, (coordinates) => value?.cells?.[getPLACubeCellKey(coordinates)]);
      if (typeof latest?.value !== "number" || !Number.isFinite(latest.value)) return;
      const phaseIndex = phases.findIndex((phase) => phase.id === latest.phase.id);
      if (phaseIndex < 0) return;
      total += latest.value;
      hasValue = true;
      oldestPhaseIndex = oldestPhaseIndex === undefined ? phaseIndex : Math.min(oldestPhaseIndex, phaseIndex);
   });
   return { value: hasValue ? total : 0, oldestPhaseIndex };
}

export function PLACubeGridModal(props: {
   project: PLAProject;
   elements: PLAElementRecord[];
   definition: PLAAttributeDefinition;
   value: any;
   filter?: PLACubeFilter;
   readOnly?: boolean;
   fullPrecision?: boolean;
   onChange?: (value: any) => void;
   onClose: () => void;
}) {
   const contextSettings = useContext(PLACubeViewContext);
   const dimensions = props.definition.cubeDimensions || [];
   const editable = !props.readOnly && Boolean(props.onChange);
   const settings = editable ? {} : contextSettings;
   const filter = editable ? undefined : props.filter;
   const timeDimensionIndex = settings.latest && !editable ? dimensions.findIndex((dimension) => dimension.elementTypeId === settings.dimensionId) : -1;
   const latestView = timeDimensionIndex >= 0;
   const dimensionOptions = useMemo(() => getPLACubeDimensionOptions(props.project, props.elements, props.definition), [props.project, props.elements, props.definition]);
   const phases = latestView ? dimensionOptions[timeDimensionIndex].elements : [];
   const [selectedKeys, setSelectedKeys] = useState<string[]>(() => normalizeCubeInstanceSelection(undefined, dimensionOptions));

   useEffect(() => {
      setSelectedKeys((previous) => normalizeCubeInstanceSelection(previous, dimensionOptions));
   }, [dimensionOptions]);

   const visibleDimensionIndexes = dimensions.map((_dimension, index) => index).filter((index) => index !== timeDimensionIndex);
   const expandedDimensionIndexes = latestView
      ? [...visibleDimensionIndexes.filter((index) => dimensions[index].expanded), ...visibleDimensionIndexes.filter((index) => !dimensions[index].expanded)].slice(0, 2)
      : visibleDimensionIndexes.filter((index) => dimensions[index].expanded).slice(0, 2);
   const selectorDimensionIndexes = visibleDimensionIndexes.filter((index) => !expandedDimensionIndexes.includes(index));
   const rowDimension = expandedDimensionIndexes[0] !== undefined ? dimensionOptions[expandedDimensionIndexes[0]] : undefined;
   const columnDimension = expandedDimensionIndexes[1] !== undefined ? dimensionOptions[expandedDimensionIndexes[1]] : undefined;
   const selectedCellKeys = normalizeCubeInstanceSelection(selectedKeys, dimensionOptions).map((key, index) => {
      const selected = filter?.[index];
      return selected && selected !== PLA_CUBE_ANY_FILTER ? selected : key;
   });
   const inputType = isPLANumberValueType(props.definition.valueType) ? "number" : props.definition.valueType === "date" ? "date" : "text";
   const showNumericTotals = isPLANumberValueType(props.definition.valueType);
   const axisKeys = (index: number | undefined) => {
      if (index === undefined) return [PLA_CUBE_TOTAL_KEY];
      const dimensionFilter = filter?.[index];
      return dimensionOptions[index].elements.filter((element) => !dimensionFilter || dimensionFilter === PLA_CUBE_ANY_FILTER || element.id === dimensionFilter).map((element) => element.id);
   };
   const rowKeys = axisKeys(expandedDimensionIndexes[0]);
   const showTotalRow = showNumericTotals && rowDimension !== undefined && canShowPLACubeTotals(props.definition, filter, settings, props.project.elementTypes) &&
      (isPLACubeDimensionTotalEnabled(rowDimension.dimension, props.project.elementTypes) || hasSelectedCubeMember(filter, expandedDimensionIndexes[0]));
   const showTotalColumn = showNumericTotals && rowDimension !== undefined && columnDimension !== undefined && canShowPLACubeTotals(props.definition, filter, settings, props.project.elementTypes) &&
      (isPLACubeDimensionTotalEnabled(columnDimension.dimension, props.project.elementTypes) || hasSelectedCubeMember(filter, expandedDimensionIndexes[1]));
   const columnKeys = axisKeys(expandedDimensionIndexes[1]);

   const updateSelectedKey = (dimensionIndex: number, key: string) => {
      setSelectedKeys((previous) => normalizeCubeInstanceSelection(previous, dimensionOptions).map((existingKey, index) => index === dimensionIndex ? key : existingKey));
   };
   const updateCell = (keys: string[], rawValue: string | boolean) => {
      props.onChange?.(setPLACubeCellValue(props.value, keys, parseCubeInputValue(rawValue, props.definition)));
   };
   const latestCell = (keys: string[]) => findPLACubeLatestCell(keys, timeDimensionIndex, phases, settings.memberId, (coordinates) => props.value?.cells?.[getPLACubeCellKey(coordinates)]);
   const renderCell = (keys: string[]) => {
      if (latestView) {
         const cell = latestCell(keys);
         return cell ? formatPLAAttributeValue(cell.value, props.definition, props.fullPrecision) : "-";
      }
      const value = getPLACubeCellValue(props.value, keys);
      const isReadOnly = props.readOnly || !props.onChange;
      if (props.definition.valueType === "boolean") return <input disabled={isReadOnly} type="checkbox" checked={Boolean(value)} onChange={(event) => updateCell(keys, event.target.checked)} />;
      if (isReadOnly && isPLANumberValueType(props.definition.valueType)) return <input disabled type="text" value={formatPLAAttributeValue(value, props.definition, props.fullPrecision)} />;
      return <input disabled={isReadOnly} type={inputType} step={inputType === "number" ? "any" : undefined} value={cubeInputValue(value)} onChange={(event) => updateCell(keys, event.target.value)} />;
   };
   const getGridCellKeys = (rowKey: string, columnKey: string): string[] => selectedCellKeys.map((key, dimensionIndex) => {
      if (dimensionIndex === expandedDimensionIndexes[0]) return rowKey;
      if (dimensionIndex === expandedDimensionIndexes[1]) return columnKey;
      return key;
   });
   const sumGridCells = (rowsToSum: string[], columnsToSum: string[]) => rowsToSum.reduce((total, rowKey) => total + columnsToSum.reduce((columnTotal, columnKey) => {
      const keys = getGridCellKeys(rowKey, columnKey);
      const numericValue = Number(latestView ? latestCell(keys)?.value : getPLACubeCellValue(props.value, keys));
      return Number.isFinite(numericValue) ? columnTotal + numericValue : columnTotal;
   }, 0), 0);
   const renderGridTotal = (rowsToSum: string[], columnsToSum: string[]) => formatPLAAttributeValue(sumGridCells(rowsToSum, columnsToSum), props.definition, editable || props.fullPrecision);
   const getTotalCellStyle = (rowsToSum: string[], columnsToSum: string[]) => {
      const phaseIndexes = latestView ? rowsToSum.flatMap((rowKey) => columnsToSum.map((columnKey) => {
         const latest = latestCell(getGridCellKeys(rowKey, columnKey));
         return typeof latest?.value === "number" && Number.isFinite(latest.value)
            ? phases.findIndex((phase) => phase.id === latest.phase.id)
            : -1;
      })).filter((index) => index >= 0) : [];
      const oldestPhaseIndex = phaseIndexes.length ? Math.min(...phaseIndexes) : -1;
      return oldestPhaseIndex >= 0 ? { color: getPLACubePhaseTextColor(oldestPhaseIndex), backgroundColor: "transparent" } : undefined;
   };

   return (
      <AdvancedDragModal title={props.definition.name || Localization.getText("Cube dimensions")} PosDefault={{ width: "820px", left: "50%", transform: "translateX(-50%)", top: "100px" }} onClose={props.onClose}>
         <ADModalBody>
            <div className="pla-cube-popup">
               {latestView ? <>
                  <p>{Localization.getText("Latest cube grid read only")}</p>
                  <PLACubePhaseLegend phases={phases} />
               </> : null}
               {selectorDimensionIndexes.length ? <div className="pla-cube-popup__selectors">
                  {selectorDimensionIndexes.map((dimensionIndex) => {
                     const option = dimensionOptions[dimensionIndex];
                     return <label key={`${option.dimension.elementTypeId}-${dimensionIndex}`}>
                        <span>{option.name}</span>
                        <select disabled={Boolean(filter?.[dimensionIndex] && filter[dimensionIndex] !== PLA_CUBE_ANY_FILTER)} value={selectedCellKeys[dimensionIndex]} onChange={(event) => updateSelectedKey(dimensionIndex, event.target.value)}>
                           {option.elements.map((element) => <option key={element.id} value={element.id}>{getPLACubeElementName(element)}</option>)}
                        </select>
                     </label>;
                  })}
               </div> : null}
               <div className="pla-cube-popup__grid-wrap">
                  <table className={`pla-cube-grid${latestView ? " pla-cube-grid--latest" : ""}`}>
                     <thead>
                        <tr>
                           {rowDimension ? <th>{rowDimension.name}</th> : null}
                           {columnDimension ? columnKeys.map((columnKey) => (
                              <th key={columnKey}>{getPLACubeElementName(columnDimension.elements.find((element) => element.id === columnKey)!)}</th>
                           )) : <th>{Localization.getText("Value")}</th>}
                           {showTotalColumn ? <th>{Localization.getText("Cube total")}</th> : null}
                        </tr>
                     </thead>
                     <tbody>
                        {rowKeys.map((rowKey) => (
                           <tr key={rowKey}>
                              {rowDimension ? <td>{getPLACubeElementName(rowDimension.elements.find((element) => element.id === rowKey)!)}</td> : null}
                              {columnKeys.map((columnKey) => {
                                 const keys = getGridCellKeys(rowKey, columnKey);
                                 const cell = latestView ? latestCell(keys) : undefined;
                                 return <td key={columnKey} title={cell ? cell.phase.name || cell.phase.id : latestView ? Localization.getText("No value through selected phase") : undefined} style={cell ? { backgroundColor: cell.color, color: "#1f2933" } : undefined}>{renderCell(keys)}</td>;
                              })}
                              {showTotalColumn ? <td className="pla-cube-grid__total" style={getTotalCellStyle([rowKey], columnKeys)}>{renderGridTotal([rowKey], columnKeys)}</td> : null}
                           </tr>
                        ))}
                        {showTotalRow ? <tr className="pla-cube-grid__total-row">
                           <td>{Localization.getText("Cube total")}</td>
                           {columnKeys.map((columnKey) => (
                              <td key={columnKey} className="pla-cube-grid__total" style={getTotalCellStyle(rowKeys, [columnKey])}>{renderGridTotal(rowKeys, [columnKey])}</td>
                           ))}
                           {showTotalColumn ? <td className="pla-cube-grid__total" style={getTotalCellStyle(rowKeys, columnKeys)}>{renderGridTotal(rowKeys, columnKeys)}</td> : null}
                        </tr> : null}
                     </tbody>
                  </table>
               </div>
            </div>
         </ADModalBody>
         <ADModalFooter><GlassButtonRow><GlassButton onClick={props.onClose}>{Localization.getText("Close")}</GlassButton></GlassButtonRow></ADModalFooter>
      </AdvancedDragModal>
   );
}

export function getPLAExpressionCubeView(value: any, definition: PLAAttributeDefinition): { value: any; definition: PLAAttributeDefinition } | undefined {
   const cube = value?.kind === "values" && Array.isArray(value.entries) && value.entries.length === 1 ? value.entries[0].value : value;
   if (!cube || typeof cube !== "object" || !cube.cells || typeof cube.cells !== "object" || !Array.isArray(cube.dimensions)) return undefined;
   return {
      value: cube,
      definition: {
         ...definition,
         type: "cube",
         valueType: cube.valueType || definition.valueType,
         cubeDimensions: (cube.dimensions as string[]).map((elementTypeId, index) => ({
            elementTypeId, expanded: index < 2
         }))
      }
   };
}

export function PLACubeAttributeView(props: {
   project: PLAProject;
   elements: PLAElementRecord[];
   definition: PLAAttributeDefinition;
   value: any;
   filter?: PLACubeFilter;
   readOnly?: boolean;
   fullPrecision?: boolean;
   onChange?: (value: any) => void;
}) {
   const settings = useContext(PLACubeViewContext);
   const { root: floatingPortalRoot } = useFloatingPortalContext();
   const [showGrid, setShowGrid] = useState(false);
   const editable = !props.readOnly && Boolean(props.onChange);
   const filter = editable ? undefined : applyPLACubeGlobalFilter(props.definition, props.filter, settings);
   const showTotal = canShowPLACubeTotals(props.definition, filter, settings, props.project.elementTypes);
   const latestSummary = getPLACubeLatestFilteredTotal(props.value, props.definition, props.elements, filter, settings);
   const filteredValue = latestSummary?.value ?? getPLACubeFilteredValue(props.value, props.definition, props.project, props.elements, filter);
   const totalValue = showTotal ? formatPLAAttributeValue(filteredValue ?? 0, props.definition, editable || props.fullPrecision) : undefined;
   const totalStyle = settings.latest && latestSummary?.oldestPhaseIndex !== undefined
      ? { color: getPLACubePhaseTextColor(latestSummary.oldestPhaseIndex) }
      : undefined;
   const showSumIndicator = !showTotal && isPLANumberValueType(props.definition.valueType);
   const hasNonZeroSum = Number.isFinite(Number(filteredValue)) && Number(filteredValue) !== 0;
   const sumIndicatorLabel = Localization.getText(hasNonZeroSum ? "Cube sum is non-zero" : "Cube sum is zero");
   return (
      <span className="pla-cube-summary">
         {showTotal ? <span style={totalStyle}>{totalValue}</span> : null}
         {showSumIndicator ? <span
            className={`pla-cube-summary__sum-indicator${hasNonZeroSum ? " pla-cube-summary__sum-indicator--nonzero" : ""}`}
            role="img"
            aria-label={sumIndicatorLabel}
            title={sumIndicatorLabel}
         /> : null}
         <GlassButton className="pla-cube-summary__expand" title={Localization.getText("Show cube values")} aria-label={Localization.getText("Show cube values")} onClick={(event) => { event.stopPropagation(); setShowGrid(true); }}>
            <MIBIcon icon="cube" />
         </GlassButton>
         {showGrid ? createPortal(<PLACubeGridModal {...props} filter={filter} onClose={() => setShowGrid(false)} />, floatingPortalRoot) : null}
      </span>
   );
}